import type { Edge, Node } from '../../../core/models/scene';
import type {
  AllocationLoadStatus,
  OrgAllocationTargetRoster,
  OrgAllocationView,
  OrgPersonAllocationLoad,
} from './OrgAllocationService';

export interface OrgAllocationPlanningSceneOptions {
  readonly origin?: { readonly x: number; readonly y: number };
  readonly idPrefix?: string;
  readonly maxAttentionPeople?: number;
  readonly maxAvailablePeople?: number;
  readonly maxTargets?: number;
}

export interface OrgAllocationPlanningScene {
  readonly nodes: readonly Node[];
  readonly edges: readonly Edge[];
  readonly hiddenAttentionPeopleCount: number;
  readonly hiddenAvailablePeopleCount: number;
  readonly hiddenTargetCount: number;
}

const DEFAULT_ORIGIN = { x: 120, y: 120 } as const;
const GRID = 12;
const KPI_WIDTH = 220;
const KPI_HEIGHT = 92;
const KPI_GAP = 20;
const LANE_TOP = 144;
const LANE_PADDING = 20;
const LANE_HEADER_RESERVE = 72;
const PERSON_CARD_WIDTH = 300;
const PERSON_CARD_HEIGHT = 180;
const PERSON_CARD_GAP = 18;
const TARGET_CARD_WIDTH = 320;
const TARGET_CARD_HEIGHT = 96;
const TARGET_CARD_GAP = 16;
const PEOPLE_LANE_WIDTH = PERSON_CARD_WIDTH + LANE_PADDING * 2;
const TARGET_LANE_WIDTH = TARGET_CARD_WIDTH + LANE_PADDING * 2;
const LANE_GAP = 28;
const DEFAULT_ATTENTION_LIMIT = 4;
const DEFAULT_AVAILABLE_LIMIT = 5;
const DEFAULT_TARGET_LIMIT = 7;

export function buildOrgAllocationPlanningScene(
  view: OrgAllocationView,
  options: OrgAllocationPlanningSceneOptions = {},
): OrgAllocationPlanningScene {
  const origin = snapPoint(options.origin ?? DEFAULT_ORIGIN);
  const idPrefix = options.idPrefix ?? `atlas-capacity-${Date.now().toString(36)}`;
  const attentionLimit = boundedLimit(options.maxAttentionPeople, DEFAULT_ATTENTION_LIMIT);
  const availableLimit = boundedLimit(options.maxAvailablePeople, DEFAULT_AVAILABLE_LIMIT);
  const targetLimit = boundedLimit(options.maxTargets, DEFAULT_TARGET_LIMIT);

  const attentionPeople = prioritizeAttentionPeople(view.people);
  const availablePeople = prioritizeAvailablePeople(view.people);
  const targets = prioritizeTargets(view.targets);
  const visibleAttentionPeople = attentionPeople.slice(0, attentionLimit);
  const visibleAvailablePeople = availablePeople.slice(0, availableLimit);
  const visibleTargets = targets.slice(0, targetLimit);

  const attentionHeight = laneHeight(visibleAttentionPeople.length, PERSON_CARD_HEIGHT, PERSON_CARD_GAP);
  const availableHeight = laneHeight(visibleAvailablePeople.length, PERSON_CARD_HEIGHT, PERSON_CARD_GAP);
  const targetsHeight = laneHeight(visibleTargets.length, TARGET_CARD_HEIGHT, TARGET_CARD_GAP);
  const laneHeightPx = Math.max(attentionHeight, availableHeight, targetsHeight, 420);

  const attentionX = origin.x;
  const availableX = attentionX + PEOPLE_LANE_WIDTH + LANE_GAP;
  const targetX = availableX + PEOPLE_LANE_WIDTH + LANE_GAP;
  const lanesY = origin.y + LANE_TOP;

  const nodes: Node[] = [
    makeKpiCard(idPrefix, 'people', origin.x, origin.y, {
      title: String(view.summary.peopleCount),
      subtitle: 'people with work context',
      accent: '#2563EB',
      fill: '#EFF6FF',
      stroke: '#BFDBFE',
    }),
    makeKpiCard(idPrefix, 'overloaded', origin.x + (KPI_WIDTH + KPI_GAP), origin.y, {
      title: String(view.summary.overAllocatedPeople),
      subtitle: 'over capacity',
      accent: view.summary.overAllocatedPeople > 0 ? '#DC2626' : '#059669',
      fill: view.summary.overAllocatedPeople > 0 ? '#FEF2F2' : '#ECFDF5',
      stroke: view.summary.overAllocatedPeople > 0 ? '#FECACA' : '#A7F3D0',
    }),
    makeKpiCard(idPrefix, 'available', origin.x + (KPI_WIDTH + KPI_GAP) * 2, origin.y, {
      title: String(view.summary.underAllocatedPeople),
      subtitle: 'with available capacity',
      accent: '#D97706',
      fill: '#FFFBEB',
      stroke: '#FDE68A',
    }),
    makeKpiCard(idPrefix, 'targets', origin.x + (KPI_WIDTH + KPI_GAP) * 3, origin.y, {
      title: String(view.summary.targetCount),
      subtitle: 'work targets consuming capacity',
      accent: '#7C3AED',
      fill: '#F5F3FF',
      stroke: '#DDD6FE',
    }),
    makeLane(idPrefix, 'attention', attentionX, lanesY, PEOPLE_LANE_WIDTH, laneHeightPx, {
      title: 'Needs attention',
      subtitle: 'Overloaded people or rows with unknown allocation',
      accent: '#EA580C',
      fill: '#FFF7ED',
      stroke: '#FDBA74',
    }),
    makeLane(idPrefix, 'available', availableX, lanesY, PEOPLE_LANE_WIDTH, laneHeightPx, {
      title: 'Available pool',
      subtitle: 'People with known spare capacity in this window',
      accent: '#059669',
      fill: '#ECFDF5',
      stroke: '#A7F3D0',
    }),
    makeLane(idPrefix, 'targets', targetX, lanesY, TARGET_LANE_WIDTH, laneHeightPx, {
      title: 'Work demand',
      subtitle: 'Targets ranked by total allocated load',
      accent: '#4F46E5',
      fill: '#EEF2FF',
      stroke: '#C7D2FE',
    }),
    ...visibleAttentionPeople.map((person, index) =>
      makePersonCard(idPrefix, person, attentionX + LANE_PADDING, laneCardY(lanesY, index, PERSON_CARD_HEIGHT, PERSON_CARD_GAP), 'attention'),
    ),
    ...visibleAvailablePeople.map((person, index) =>
      makePersonCard(idPrefix, person, availableX + LANE_PADDING, laneCardY(lanesY, index, PERSON_CARD_HEIGHT, PERSON_CARD_GAP), 'available'),
    ),
    ...visibleTargets.map((target, index) =>
      makeTargetCard(idPrefix, target, targetX + LANE_PADDING, laneCardY(lanesY, index, TARGET_CARD_HEIGHT, TARGET_CARD_GAP)),
    ),
  ];

  if (visibleAttentionPeople.length === 0) {
    nodes.push(makeLaneNote(idPrefix, 'attention-empty', attentionX, lanesY, PEOPLE_LANE_WIDTH, 'No overloaded or unknown allocations.'));
  }
  if (visibleAvailablePeople.length === 0) {
    nodes.push(makeLaneNote(idPrefix, 'available-empty', availableX, lanesY, PEOPLE_LANE_WIDTH, 'No known spare capacity in this window.'));
  }
  if (visibleTargets.length === 0) {
    nodes.push(makeLaneNote(idPrefix, 'targets-empty', targetX, lanesY, TARGET_LANE_WIDTH, 'No active work targets for this date.'));
  }

  return {
    nodes,
    edges: [],
    hiddenAttentionPeopleCount: Math.max(0, attentionPeople.length - visibleAttentionPeople.length),
    hiddenAvailablePeopleCount: Math.max(0, availablePeople.length - visibleAvailablePeople.length),
    hiddenTargetCount: Math.max(0, targets.length - visibleTargets.length),
  };
}

function makeKpiCard(
  prefix: string,
  key: string,
  x: number,
  y: number,
  input: { readonly title: string; readonly subtitle: string; readonly accent: string; readonly fill: string; readonly stroke: string },
): Node {
  return {
    id: diagramId(prefix, 'kpi', key),
    type: 'org-shape',
    category: 'orgchart',
    x,
    y,
    width: KPI_WIDTH,
    height: KPI_HEIGHT,
    w: KPI_WIDTH,
    h: KPI_HEIGHT,
    zIndex: 5,
    attrs: {
      variant: 'role',
      title: input.title,
      subtitle: input.subtitle,
      fillColor: input.fill,
      headerColor: input.accent,
      strokeColor: input.stroke,
      strokeWidth: 1,
      textColor: '#0F172A',
      subtitleColor: '#334155',
      atlasPlanning: { kind: 'kpi', key },
    },
  } as Node;
}

function makeLane(
  prefix: string,
  key: string,
  x: number,
  y: number,
  width: number,
  height: number,
  input: { readonly title: string; readonly subtitle: string; readonly accent: string; readonly fill: string; readonly stroke: string },
): Node {
  return {
    id: diagramId(prefix, 'lane', key),
    type: 'org-shape',
    category: 'orgchart',
    x,
    y,
    width,
    height,
    w: width,
    h: height,
    zIndex: 0,
    attrs: {
      variant: 'department',
      title: input.title,
      subtitle: input.subtitle,
      fillColor: input.fill,
      headerColor: input.accent,
      strokeColor: input.stroke,
      strokeWidth: 1,
      textColor: '#0F172A',
      subtitleColor: '#475569',
      atlasPlanning: { kind: 'lane', key },
    },
  } as Node;
}

function makePersonCard(
  prefix: string,
  person: OrgPersonAllocationLoad,
  x: number,
  y: number,
  lane: 'attention' | 'available',
): Node {
  const status = personStatusPalette(person.status);
  const topTargets = summarizePersonTargets(person);
  return {
    id: diagramId(prefix, 'person', `${lane}:${person.personExternalId}`),
    type: 'org_person_text',
    category: 'orgchart',
    name: person.personLabel,
    x,
    y,
    width: PERSON_CARD_WIDTH,
    height: PERSON_CARD_HEIGHT,
    w: PERSON_CARD_WIDTH,
    h: PERSON_CARD_HEIGHT,
    zIndex: 10,
    attrs: {
      personId: person.personExternalId,
      name: person.personLabel,
      role: `${formatAllocation(person.totalAllocation)} load - ${person.assignmentCount} commitment${person.assignmentCount === 1 ? '' : 's'}`,
      fillColor: '#FFFFFF',
      accentColor: status.accent,
      borderColor: status.border,
      strokeColor: status.border,
      strokeWidth: 2,
      textColor: '#04263D',
      subtitleColor: status.accent,
      customFields: [
        { id: 'atlasWorkloadStatus', name: 'Status', type: 'text', value: status.label, showOnCard: true },
        { id: 'atlasWorkTargets', name: 'Work', type: 'text', value: topTargets, showOnCard: true },
      ],
      visibleFields: {
        custom_atlasWorkloadStatus: true,
        custom_atlasWorkTargets: true,
      },
      atlasPlanning: {
        kind: 'person_card',
        lane,
        personExternalId: person.personExternalId,
        totalAllocation: person.totalAllocation,
        assignmentCount: person.assignmentCount,
        hasUnspecifiedAllocation: person.hasUnspecifiedAllocation,
        status: person.status,
        topTargets,
      },
    },
  } as Node;
}

function makeTargetCard(
  prefix: string,
  target: OrgAllocationTargetRoster,
  x: number,
  y: number,
): Node {
  const palette = targetPalette(target.targetKind);
  const demand = target.hasUnspecifiedAllocation
    ? `${formatAllocation(target.totalAllocation)} + unknown`
    : formatAllocation(target.totalAllocation);
  return {
    id: diagramId(prefix, 'target', target.targetExternalId),
    type: 'org_project',
    category: 'orgchart',
    name: target.targetLabel,
    x,
    y,
    width: TARGET_CARD_WIDTH,
    height: TARGET_CARD_HEIGHT,
    w: TARGET_CARD_WIDTH,
    h: TARGET_CARD_HEIGHT,
    zIndex: 10,
    attrs: {
      name: target.targetLabel,
      projectType: target.targetKind,
      status: `${humanizeIdentifier(target.targetKind)} - ${target.peopleCount} people - ${demand}`,
      memberCount: target.peopleCount,
      completion: Math.round(target.totalAllocation * 100),
      customFields: [],
      accentColor: palette.accentColor,
      fillColor: palette.fillColor,
      textColor: palette.textColor,
      borderColor: palette.borderColor,
      atlasPlanning: {
        kind: 'target_card',
        targetExternalId: target.targetExternalId,
        targetKind: target.targetKind,
        totalAllocation: target.totalAllocation,
        peopleCount: target.peopleCount,
        hasUnspecifiedAllocation: target.hasUnspecifiedAllocation,
      },
    },
  } as Node;
}

function makeLaneNote(prefix: string, key: string, laneX: number, laneY: number, laneWidth: number, text: string): Node {
  return {
    id: diagramId(prefix, 'note', key),
    type: 'text',
    x: laneX + LANE_PADDING,
    y: laneY + LANE_HEADER_RESERVE,
    width: laneWidth - LANE_PADDING * 2,
    height: 64,
    w: laneWidth - LANE_PADDING * 2,
    h: 64,
    zIndex: 10,
    fillColor: 'rgba(255,255,255,0.72)',
    strokeColor: '#CBD5E1',
    strokeWidth: 1,
    text,
    attrs: {
      text,
      fontSize: 14,
      textAlign: 'center',
      verticalAlign: 'middle',
      textColor: '#64748B',
      atlasPlanning: { kind: 'empty_note', key },
    },
  };
}

function laneHeight(count: number, cardHeight: number, gap: number): number {
  if (count <= 0) return LANE_HEADER_RESERVE + LANE_PADDING + 92;
  return LANE_HEADER_RESERVE + LANE_PADDING + count * cardHeight + Math.max(0, count - 1) * gap + LANE_PADDING;
}

function laneCardY(laneY: number, index: number, cardHeight: number, gap: number): number {
  return laneY + LANE_HEADER_RESERVE + index * (cardHeight + gap);
}

function prioritizeAttentionPeople(people: readonly OrgPersonAllocationLoad[]): OrgPersonAllocationLoad[] {
  return people
    .filter((person) => person.status === 'over_allocated' || person.status === 'unknown')
    .sort((a, b) => {
      const byStatus = attentionRank(a.status) - attentionRank(b.status);
      if (byStatus !== 0) return byStatus;
      const byLoad = b.totalAllocation - a.totalAllocation;
      if (byLoad !== 0) return byLoad;
      return a.personLabel.localeCompare(b.personLabel);
    });
}

function prioritizeAvailablePeople(people: readonly OrgPersonAllocationLoad[]): OrgPersonAllocationLoad[] {
  return people
    .filter((person) => person.status === 'under_allocated')
    .sort((a, b) => {
      const bySpare = a.totalAllocation - b.totalAllocation;
      if (bySpare !== 0) return bySpare;
      return a.personLabel.localeCompare(b.personLabel);
    });
}

function prioritizeTargets(targets: readonly OrgAllocationTargetRoster[]): OrgAllocationTargetRoster[] {
  return [...targets].sort((a, b) => {
    const byLoad = b.totalAllocation - a.totalAllocation;
    if (byLoad !== 0) return byLoad;
    const byPeople = b.peopleCount - a.peopleCount;
    if (byPeople !== 0) return byPeople;
    return a.targetLabel.localeCompare(b.targetLabel);
  });
}

function summarizePersonTargets(person: OrgPersonAllocationLoad): string {
  const labels = [...new Set(person.assignments.map((assignment) => assignment.targetLabel).filter(Boolean))]
    .slice(0, 2);
  if (labels.length === 0) return 'No named target';
  const remaining = person.assignments.length - labels.length;
  return remaining > 0 ? `${labels.join(', ')} +${remaining}` : labels.join(', ');
}

function personStatusPalette(status: AllocationLoadStatus): {
  readonly label: string;
  readonly accent: string;
  readonly border: string;
} {
  switch (status) {
    case 'over_allocated':
      return { label: 'Over capacity', accent: '#DC2626', border: '#FCA5A5' };
    case 'fully_allocated':
      return { label: 'At capacity', accent: '#059669', border: '#A7F3D0' };
    case 'unknown':
      return { label: 'Unknown allocation', accent: '#64748B', border: '#CBD5E1' };
    case 'under_allocated':
    default:
      return { label: 'Available capacity', accent: '#D97706', border: '#FDE68A' };
  }
}

function targetPalette(kind: string): {
  readonly accentColor: string;
  readonly fillColor: string;
  readonly borderColor: string;
  readonly textColor: string;
} {
  switch (kind) {
    case 'process':
      return { accentColor: '#0F766E', fillColor: '#ECFDF5', borderColor: '#99F6E4', textColor: '#134E4A' };
    case 'service':
      return { accentColor: '#0369A1', fillColor: '#EFF6FF', borderColor: '#BAE6FD', textColor: '#0C4A6E' };
    case 'system':
      return { accentColor: '#7C2D12', fillColor: '#FFF7ED', borderColor: '#FED7AA', textColor: '#7C2D12' };
    case 'objective':
      return { accentColor: '#7C3AED', fillColor: '#F5F3FF', borderColor: '#DDD6FE', textColor: '#4C1D95' };
    case 'project':
    default:
      return { accentColor: '#4F46E5', fillColor: '#EEF2FF', borderColor: '#C7D2FE', textColor: '#312E81' };
  }
}

function attentionRank(status: AllocationLoadStatus): number {
  if (status === 'over_allocated') return 0;
  if (status === 'unknown') return 1;
  return 2;
}

function boundedLimit(value: number | undefined, fallback: number): number {
  if (value == null || !Number.isFinite(value)) return fallback;
  return Math.max(1, Math.floor(value));
}

function snapPoint(point: { readonly x: number; readonly y: number }): { readonly x: number; readonly y: number } {
  return {
    x: Math.round(point.x / GRID) * GRID,
    y: Math.round(point.y / GRID) * GRID,
  };
}

function diagramId(prefix: string, kind: string, value: string): string {
  return `${prefix}-${kind}-${stableToken(value)}`;
}

function stableToken(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 36) || 'item';
  return `${slug}-${hashString(value)}`;
}

function hashString(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) % 2_147_483_647;
  }
  return Math.abs(hash).toString(36);
}

function formatAllocation(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function humanizeIdentifier(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
